// frontend/src/components/layout/Layout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FiShoppingCart,
  FiUser,
  FiSearch,
  FiHeart,
  FiMenu,
  FiX,
  FiHome,
  FiGrid,
  FiPackage,
  FiLogOut,
  FiSettings
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import CartSidebar from '../cart/CartSidebar';
import { FaWhatsapp, FaFacebookF, FaInstagram, FaTiktok } from 'react-icons/fa';

export default function Layout() {
  const { user, logout } = useAuth();
  const items = useCart((state) => state.items);
  const cartCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const [scrolled, setScrolled] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchHidden, setSearchHidden] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let lastScroll = 0;

    const onScroll = () => {
      const currentScroll = window.scrollY;
      setScrolled(currentScroll > 20);
      setSearchHidden(currentScroll > lastScroll && currentScroll > 100);
      lastScroll = currentScroll;
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setUserMenuOpen(false);
  }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();

    if (searchQuery.trim()) {
      navigate(`/produits?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0a] text-gray-900 dark:text-white w-full max-w-full overflow-x-hidden">
      {/* Top banner */}
      <div className="bg-primary-700 text-white text-center py-2 text-xs font-medium">
        Livraison offerte à partir de 100 000 FCFA au Sénégal
      </div>

      {/* Navbar */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          scrolled
            ? 'bg-white/95 dark:bg-[#0a0a0a]/95 backdrop-blur-xl border-gray-200 dark:border-[#1f1f1f] shadow-sm dark:shadow-black/20'
            : 'bg-white dark:bg-[#0a0a0a] border-gray-200 dark:border-[#1f1f1f]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-12 md:h-14">
            {/* Logo */}
            {/* Logo */}
            <Link
              to="/"
              onClick={scrollToTop}
              className="flex items-center gap-2 flex-shrink-0"
            >
              <img
                src="/LogoSunuMarket.svg"
                alt="SunuMarket Logo"
                className="h-12 md:h-14 w-auto object-contain py-1 px-2 dark:invert dark:hue-rotate-180 dark:saturate-150" 
              />
            </Link>

            {/* Search desktop */}
            <form
              onSubmit={handleSearch}
              className={`hidden md:flex flex-1 max-w-md mx-8 transition-all duration-300 ${
                searchHidden
                  ? '-translate-y-full opacity-0 absolute pointer-events-none'
                  : 'translate-y-0 opacity-100'
              }`}
            >
              <div className="relative w-full">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />

                <input
                  type="text"
                  placeholder="Rechercher des produits..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-[#111111] border border-gray-300 dark:border-[#1f1f1f] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-xl pl-10 pr-4 py-1.5 md:py-2 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
            </form>

            {/* Nav links desktop */}
            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/produits"
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Produits
              </Link>

              <button
                onClick={() => setCartOpen(true)}
                className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                aria-label="Ouvrir le panier"
              >
                <FiShoppingCart className="w-5 h-5" />

                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>

              {user && (
                <Link
                  to="/wishlist"
                  className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                  aria-label="Liste de souhaits"
                >
                  <FiHeart className="w-5 h-5" />
                </Link>
              )}

              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 ml-2 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[#111111] transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-sm font-bold text-white">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>

                    <span className="text-sm text-gray-700 dark:text-gray-300 hidden lg:block">
                      {user.name?.split(' ')[0]}
                    </span>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#1f1f1f] rounded-2xl shadow-xl dark:shadow-black/40 overflow-hidden animate-scale-in">
                      <div className="p-3 border-b border-gray-200 dark:border-[#1f1f1f]">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {user.name}
                        </p>

                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user.email}
                        </p>
                      </div>

                      <div className="p-1">
                        <Link
                          to="/profil"
                          className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                        >
                          <FiUser className="w-4 h-4" />
                          Mon Profil
                        </Link>

                        <Link
                          to="/profil?tab=orders"
                          className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                        >
                          <FiPackage className="w-4 h-4" />
                          Mes Commandes
                        </Link>

                        {user.role === 'admin' && (
                          <Link
                            to="/admin"
                            className="flex items-center gap-3 px-3 py-2.5 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                          >
                            <FiSettings className="w-4 h-4" />
                            Administration
                          </Link>
                        )}

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                        >
                          <FiLogOut className="w-4 h-4" />
                          Déconnexion
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 ml-2">
                  <Link to="/login" className="btn-ghost text-sm">
                    Connexion
                  </Link>

                  <Link to="/register" className="btn-primary text-sm py-2 px-4">
                    S'inscrire
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile buttons */}
            <div className="flex md:hidden items-center gap-2">
              <button
                onClick={() => setCartOpen(true)}
                className="relative p-2 text-gray-600 dark:text-gray-300"
                aria-label="Ouvrir le panier"
              >
                <FiShoppingCart className="w-5 h-5" />

                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 text-gray-600 dark:text-gray-300"
                aria-label="Menu"
              >
                {menuOpen ? (
                  <FiX className="w-5 h-5" />
                ) : (
                  <FiMenu className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile search */}
          <div className="md:hidden pb-3">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />

                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-[#111111] border border-gray-300 dark:border-[#1f1f1f] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-xl pl-10 pr-4 py-1.5 text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
            </form>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-[#1f1f1f] bg-white dark:bg-[#111111] animate-slide-up">
            <div className="px-4 py-3 space-y-1">
              <Link
                to="/"
                onClick={scrollToTop}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
              >
                <FiHome className="w-4 h-4" />
                Accueil
              </Link>

              <Link
                to="/produits"
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
              >
                <FiGrid className="w-4 h-4" />
                Produits
              </Link>

              {user ? (
                <>
                  <Link
                    to="/profil"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                  >
                    <FiUser className="w-4 h-4" />
                    Mon Profil
                  </Link>

                  <Link
                    to="/profil?tab=orders"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                  >
                    <FiPackage className="w-4 h-4" />
                    Mes Commandes
                  </Link>

                  <Link
                    to="/wishlist"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                  >
                    <FiHeart className="w-4 h-4" />
                    Liste de souhaits
                  </Link>

                  {user.role === 'admin' && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                    >
                      <FiSettings className="w-4 h-4" />
                      Admin
                    </Link>
                  )}

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-500 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
                  >
                    <FiLogOut className="w-4 h-4" />
                    Déconnexion
                  </button>
                </>
              ) : (
                <div className="flex gap-2 pt-2">
                  <Link
                    to="/login"
                    className="flex-1 btn-secondary text-center text-sm py-2"
                  >
                    Connexion
                  </Link>

                  <Link
                    to="/register"
                    className="flex-1 btn-primary text-center text-sm py-2"
                  >
                    S'inscrire
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Page content */}
      <main className="mobile-padding pb-20 md:pb-0 bg-white dark:bg-[#0a0a0a]">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 dark:bg-[#111111] border-t border-gray-200 dark:border-[#1f1f1f] mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {/* Colonne 1 */}
            <div className="col-span-2 sm:col-span-2 md:col-span-1">
              <Link
                to="/"
                onClick={scrollToTop}
                className="flex items-center flex-shrink-0 mb-3"
              >
                <img
                  src="/LogoSunuMarket.svg"
                  alt="SunuMarket Logo"
                  className="h-12 w-auto object-contain py-1 px-0 dark:invert dark:hue-rotate-180 dark:saturate-150"
                />
              </Link>

              <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed mb-4">
                La plateforme de confiance pour acheter au Sénégal. Livraison rapide partout.
              </p>

              <div className="flex gap-2">
                <a
                  href="https://www.facebook.com/profile.php?id=61583686427595"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#1877F2] hover:bg-[#1865d3] text-white flex items-center justify-center transition-transform hover:scale-110"
                >
                  <FaFacebookF size={14} />
                </a>

                <a
                  href="https://www.instagram.com/sunu_market221"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white flex items-center justify-center transition-transform hover:scale-110"
                >
                  <FaInstagram size={14} />
                </a>

                <a
                  href="https://www.tiktok.com/@sunu_market221"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-black border border-gray-700 text-white flex items-center justify-center transition-transform hover:scale-110"
                >
                  <FaTiktok size={14} />
                </a>

                <a
                  href="https://wa.me/221786998712"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#25D366] hover:bg-[#1da851] text-white flex items-center justify-center transition-transform hover:scale-110"
                >
                  <FaWhatsapp size={14} />
                </a>
              </div>
            </div>

            {/* Colonne 2 */}
            <div>
              <h4 className="text-gray-900 dark:text-white font-semibold mb-3 text-sm sm:text-base">
                Boutique
              </h4>

              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <li>
                  <Link to="/produits" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    Tous les produits
                  </Link>
                </li>
                <li>
                  <Link to="/produits?category=electronique" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    Électronique
                  </Link>
                </li>
                <li>
                  <Link to="/produits?category=mode" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    Mode
                  </Link>
                </li>
                <li>
                  <Link to="/produits?category=maison" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    Maison
                  </Link>
                </li>
              </ul>
            </div>

            {/* Colonne 3 */}
            <div>
              <h4 className="text-gray-900 dark:text-white font-semibold mb-3 text-sm sm:text-base">
                Compte
              </h4>

              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <li>
                  <Link to="/login" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    Connexion
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    Créer un compte
                  </Link>
                </li>
                <li>
                  <Link to="/profil?tab=orders" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    Mes commandes
                  </Link>
                </li>
              </ul>
            </div>

            {/* Colonne 4 */}
            <div className="col-span-2 sm:col-span-2 md:col-span-1">
              <h4 className="text-gray-900 dark:text-white font-semibold mb-3 text-sm sm:text-base">
                Contact
              </h4>

              <ul className="space-y-1.5 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                <li className="flex items-center gap-1.5">
                  <span>📍</span>
                  Dakar, Sénégal
                </li>

                <li>
                  <a href="tel:+221786998712" className="text-blue-600 dark:text-blue-400 hover:underline">
                    +221 78 699 87 12
                  </a>
                </li>

                <li>
                  <a href="tel:+221338034395" className="text-blue-600 dark:text-blue-400 hover:underline">
                    +221 33 803 43 95
                  </a>
                </li>

                <li>
                  <a
                    href="mailto:sunumarket01@gmail.com"
                    className="text-blue-600 dark:text-blue-400 hover:underline text-xs break-all"
                  >
                    sunumarket01@gmail.com
                  </a>
                </li>
              </ul>

              <a
                href="https://wa.me/221786998712"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-lg transition text-sm w-full sm:w-auto max-w-full truncate"
              >
                <FaWhatsapp size={18} />
                <span className="font-medium">WhatsApp</span>
              </a>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-[#1f1f1f] mt-6 sm:mt-8 pt-4 text-center">
            <p className="text-gray-500 dark:text-gray-500 text-xs sm:text-sm">
              © 2026 SunuMarket. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white dark:bg-[#111111] border-t border-gray-200 dark:border-[#1f1f1f] z-40">
        <div className="flex items-center justify-around h-16">
          <Link
            to="/"
            onClick={scrollToTop}
            className="flex flex-col items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors p-2"
          >
            <FiHome className="w-5 h-5" />
            <span className="text-xs">Accueil</span>
          </Link>

          <Link
            to="/produits"
            className="flex flex-col items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors p-2"
          >
            <FiGrid className="w-5 h-5" />
            <span className="text-xs">Produits</span>
          </Link>

          <button
            onClick={() => setCartOpen(true)}
            className="flex flex-col items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors p-2 relative"
          >
            <FiShoppingCart className="w-5 h-5" />
            <span className="text-xs">Panier</span>

            {cartCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            )}
          </button>

          <Link
            to={user ? '/profil' : '/login'}
            className="flex flex-col items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors p-2"
          >
            <FiUser className="w-5 h-5" />
            <span className="text-xs">{user ? 'Profil' : 'Compte'}</span>
          </Link>
        </div>
      </div>

      <CartSidebar open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}